import { useCallback, useState } from 'react'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

type SignOutStatus = 'idle' | 'pending' | 'error'

/**
 * Signs out, tracked under `source`. Never rejects: a failure is logged and
 * surfaced as `isError` for the caller to show.
 */
export function useSignOut(source: string) {
  const [status, setStatus] = useState<SignOutStatus>('idle')

  const signOut = useCallback(async () => {
    setStatus('pending')
    AmplitudeRenderer.trackEvent('Auth', { action: 'Sign Out', source })
    try {
      await AuthIpcRenderer.signOut()
      setStatus('idle')
    } catch (error) {
      console.error('Failed to sign out:', error)
      setStatus('error')
    }
  }, [source])

  // Leaves a sign-out in flight alone, so closing its dialog can't re-enable a second one.
  const reset = useCallback(() => setStatus((prev) => (prev === 'pending' ? prev : 'idle')), [])

  return { signOut, isPending: status === 'pending', isError: status === 'error', reset }
}
